import { useState, useEffect, useRef } from 'react';
import { Camera } from 'lucide-react';
import { ShrinkWrapLabel } from './ShrinkWrapLabel';

interface ProfilePreviewCardProps {
  photoUrl?: string;
  vertical: 'dating' | 'linkedin';
  name?: string;
  verdict?: string;
  traits?: { name: string; value: number }[];
  className?: string;
}

/** Default card heights before pretext adjustment */
const BASE_CARD_HEIGHT = { dating: 420, linkedin: 180 };

/** Extra height added per additional wrapped line */
const LINE_OVERFLOW_PX = 24;

/**
 * Simulated profile card showing how a photo would appear
 * in a dating app (Hinge/Tinder) or LinkedIn profile header.
 *
 * Uses pretext `measureText()` to predict verdict text wrap
 * and adjust card height. Falls back to CSS overflow-hidden
 * with ellipsis when pretext is unavailable.
 */
export function ProfilePreviewCard({
  photoUrl,
  vertical,
  name,
  verdict,
  traits,
  className = '',
}: ProfilePreviewCardProps) {
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const verdictRef = useRef<HTMLDivElement>(null!);

  // Pretext measurement for verdict text fitting
  useEffect(() => {
    if (!verdict) return;

    let cancelled = false;

    const measure = async () => {
      try {
        const { measureText } = await import('../../lib/pretext/measure');
        const { buildFontString } = await import('../../lib/pretext/fonts');

        const font = buildFontString(400, 14, 'ui');
        const maxWidth = vertical === 'dating' ? 280 : 240;
        const lineHeight = 20;

        const result = await measureText(verdict, font, maxWidth, lineHeight);
        if (cancelled || !result) return;

        // If text wraps beyond one line, expand the card
        if (result.lineCount > 1) {
          const extraLines = result.lineCount - 1;
          const base = BASE_CARD_HEIGHT[vertical];
          setCardHeight(base + extraLines * LINE_OVERFLOW_PX);
        }
      } catch {
        // Pretext unavailable -- CSS fallback handles it
      }
    };

    measure();
    return () => { cancelled = true; };
  }, [verdict, vertical]);

  if (vertical === 'dating') {
    return (
      <DatingCard
        photoUrl={photoUrl}
        name={name}
        verdict={verdict}
        traits={traits}
        cardHeight={cardHeight}
        verdictRef={verdictRef}
        className={className}
      />
    );
  }

  return (
    <LinkedInCard
      photoUrl={photoUrl}
      name={name}
      verdict={verdict}
      traits={traits}
      cardHeight={cardHeight}
      verdictRef={verdictRef}
      className={className}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Dating variant                                                     */
/* ------------------------------------------------------------------ */

interface VariantProps {
  photoUrl?: string;
  name?: string;
  verdict?: string;
  traits?: { name: string; value: number }[];
  cardHeight: number | null;
  verdictRef: React.RefObject<HTMLDivElement>;
  className: string;
}

function DatingCard({
  photoUrl,
  name,
  verdict,
  traits,
  cardHeight,
  verdictRef,
  className,
}: VariantProps) {
  const height = cardHeight ?? BASE_CARD_HEIGHT.dating;

  return (
    <div
      data-testid="profile-preview-card"
      data-variant="dating"
      className={`relative max-w-[320px] overflow-hidden rounded-xl bg-surface-warm shadow-card ${className}`}
      style={{ height }}
    >
      {/* Rose accent swipe indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent" />

      {/* Photo area -- upper 60% */}
      <div className="relative h-[60%] w-full overflow-hidden bg-surface-muted">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt="Profile preview"
            className="h-full w-full object-cover"
          />
        ) : (
          <PhotoPlaceholder />
        )}
      </div>

      {/* Info area -- lower 40% */}
      <div className="flex flex-col gap-2 px-4 py-3">
        {name && (
          <h3 className="font-serif text-lg text-ink-strong leading-tight truncate">
            {name}
          </h3>
        )}

        {verdict && (
          <div
            ref={verdictRef}
            className="text-sm text-ink-muted leading-snug line-clamp-2"
          >
            {verdict}
          </div>
        )}

        {traits && traits.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {traits.slice(0, 3).map((t) => (
              <ShrinkWrapLabel key={t.name} text={t.name} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  LinkedIn variant                                                   */
/* ------------------------------------------------------------------ */

function LinkedInCard({
  photoUrl,
  name,
  verdict,
  traits,
  cardHeight,
  verdictRef,
  className,
}: VariantProps) {
  const height = cardHeight ?? BASE_CARD_HEIGHT.linkedin;

  return (
    <div
      data-testid="profile-preview-card"
      data-variant="linkedin"
      className={`relative max-w-[400px] overflow-hidden rounded-lg bg-surface shadow-card ${className}`}
      style={{ height }}
    >
      {/* Blue brand bar */}
      <div className="absolute left-0 right-0 top-0 h-1 bg-accent-secondary" />

      {/* Content row */}
      <div className="flex items-start gap-4 px-5 pt-5 pb-4">
        {/* Circular photo */}
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-surface-muted">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Profile preview"
              className="h-full w-full object-cover"
            />
          ) : (
            <PhotoPlaceholder compact />
          )}
        </div>

        {/* Text content */}
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          {name && (
            <h3 className="font-sans text-base font-bold text-ink-strong leading-tight truncate">
              {name}
            </h3>
          )}

          {verdict && (
            <div
              ref={verdictRef}
              className="text-sm text-ink-muted leading-snug line-clamp-2"
            >
              {verdict}
            </div>
          )}

          {traits && traits.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {traits.slice(0, 3).map((t) => (
                <ShrinkWrapLabel key={t.name} text={t.name} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared photo placeholder                                           */
/* ------------------------------------------------------------------ */

function PhotoPlaceholder({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-surface-muted">
      <Camera
        className={`text-ink-faint ${compact ? 'h-6 w-6' : 'h-10 w-10'}`}
        strokeWidth={1.5}
      />
    </div>
  );
}
